<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ConfigForm from '../components/ConfigForm.vue';
import AnswerForm from '../components/AnswerForm.vue';
import AnswerTable from '../components/AnswerTable.vue';
import UnansweredTable from '../components/UnansweredTable.vue';
import UnansweredConvertForm from '../components/UnansweredConvertForm.vue';
import AnswerImportModal from '../components/AnswerImportModal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import StudioSection from '../components/StudioSection.vue';
import StudioEmptyState from '../components/StudioEmptyState.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const configFields = [
  {
    path: 'answers.fuzzy_enabled',
    label: 'AI_AGENT.ANSWERS.FIELDS.FUZZY_ENABLED',
    type: 'switch',
  },
  {
    path: 'answers.fuzzy_threshold',
    label: 'AI_AGENT.ANSWERS.FIELDS.FUZZY_THRESHOLD',
    type: 'number',
    section: 'ADVANCED',
  },
  {
    path: 'answers.context_rows_cap',
    label: 'AI_AGENT.ANSWERS.FIELDS.CONTEXT_ROWS_CAP',
    type: 'number',
    section: 'ADVANCED',
  },
];

// Numeric thresholds live in a collapsed card; the fuzzy switch stays visible.
// Built lazily: the account locale may load after this tab is set up.
const configSections = computed(() => [
  {
    key: 'ADVANCED',
    title: t('AI_AGENT.STUDIO.ADVANCED'),
    description: t('AI_AGENT.STUDIO.ADVANCED_HELP'),
    icon: 'i-lucide-sliders-horizontal',
    collapsible: true,
  },
]);

const activeSubTab = ref('exact');
const subTabItems = computed(() => [
  { key: 'exact', label: t('AI_AGENT.ANSWERS.SUB_TABS.EXACT') },
  { key: 'keyword', label: t('AI_AGENT.ANSWERS.SUB_TABS.KEYWORD') },
  { key: 'unanswered', label: t('AI_AGENT.ANSWERS.SUB_TABS.UNANSWERED') },
]);
const activeSubTabIndex = computed(() =>
  Math.max(
    0,
    subTabItems.value.findIndex(tab => tab.key === activeSubTab.value)
  )
);

// ---- exact / keyword answers (aiBotAnswers store) -----------------------------
const answers = useMapGetter('aiBotAnswers/getRecords');
const answersFlags = useMapGetter('aiBotAnswers/getUIFlags');

const answersForSubTab = computed(() => answers.value);

const fetchAnswers = () => {
  store.dispatch('aiBotAnswers/get', {
    botId: props.bot.id,
    matchType: activeSubTab.value,
  });
};

const formDialogRef = ref(null);
const editingAnswer = ref(null);
const formMatchType = ref('exact');

const openCreateDialog = () => {
  formMatchType.value = activeSubTab.value;
  editingAnswer.value = null;
  formDialogRef.value.open();
};

const openEditDialog = record => {
  formMatchType.value = record.match_type;
  editingAnswer.value = record;
  formDialogRef.value.open();
};

// Pending rows only, one category at a time: the tab opens on the questions a
// client can answer by writing text (gap).
const unansweredCategory = ref('gap');
const unansweredPage = ref(1);

const fetchUnanswered = () => {
  store.dispatch('aiBotUnanswered/get', {
    botId: props.bot.id,
    status: 'pending',
    category: unansweredCategory.value,
    page: unansweredPage.value,
  });
};

const saveAnswer = async payload => {
  const action = editingAnswer.value
    ? store.dispatch('aiBotAnswers/update', {
        botId: props.bot.id,
        answerId: editingAnswer.value.id,
        ...payload,
      })
    : store.dispatch('aiBotAnswers/create', {
        botId: props.bot.id,
        ...payload,
      });
  const saved = await action;
  if (saved) {
    useAlert(t('AI_AGENT.ANSWERS.SAVED'));
    formDialogRef.value.close();
    fetchAnswers();
  }
};

const toggleAnswer = record => {
  store
    .dispatch('aiBotAnswers/update', {
      botId: props.bot.id,
      answerId: record.id,
      enabled: !record.enabled,
    })
    .then(fetchAnswers);
};

const deleteDialogRef = ref(null);
const deletingAnswer = ref(null);

const openDeleteDialog = record => {
  deletingAnswer.value = record;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  const deleted = await store.dispatch('aiBotAnswers/delete', {
    botId: props.bot.id,
    answerId: deletingAnswer.value.id,
  });
  if (deleted) {
    useAlert(t('AI_AGENT.ANSWERS.DELETED'));
    deleteDialogRef.value.close();
    fetchAnswers();
  }
};

const importDialogRef = ref(null);
const onImported = () => fetchAnswers();

// ---- unanswered review loop (aiBotUnanswered store) ---------------------------
const unanswered = useMapGetter('aiBotUnanswered/getRecords');
const unansweredMeta = useMapGetter('aiBotUnanswered/getMeta');
const unansweredFlags = useMapGetter('aiBotUnanswered/getUIFlags');

const CATEGORY_FILTERS = computed(() =>
  ['gap', 'needs_api', 'handover'].map(key => ({
    key,
    label: t(`AI_AGENT.UNANSWERED.CATEGORY.${key.toUpperCase()}`),
    count: unansweredMeta.value.category_counts?.[key] ?? 0,
  }))
);

const selectUnansweredCategory = category => {
  unansweredCategory.value = category;
  unansweredPage.value = 1;
  fetchUnanswered();
};

const unansweredTotalPages = computed(() =>
  Math.max(1, Math.ceil((unansweredMeta.value.count || 0) / 25))
);

const gotoUnansweredPage = page => {
  unansweredPage.value = page;
  fetchUnanswered();
};

const convertDialogRef = ref(null);
const convertingRow = ref(null);
const isConverting = ref(false);

const openConvertDialog = record => {
  convertingRow.value = record;
  convertDialogRef.value.open();
};

const saveConversion = async ({ keywords, answer }) => {
  isConverting.value = true;
  const saved = await store.dispatch('aiBotUnanswered/promote', {
    botId: props.bot.id,
    id: convertingRow.value.id,
    keywords,
    answer,
  });
  isConverting.value = false;
  if (saved) {
    useAlert(t('AI_AGENT.UNANSWERED.CONVERTED'));
    convertDialogRef.value.close();
    fetchUnanswered();
  }
};

const dismissRow = async record => {
  const dismissed = await store.dispatch('aiBotUnanswered/dismiss', {
    botId: props.bot.id,
    id: record.id,
  });
  if (dismissed) {
    useAlert(t('AI_AGENT.UNANSWERED.DISMISSED'));
    fetchUnanswered();
  }
};

const onSubTabChanged = tab => {
  activeSubTab.value = tab.key;
  if (tab.key === 'unanswered') {
    fetchUnanswered();
  } else {
    fetchAnswers();
  }
};

watch(
  () => props.bot.id,
  () => {
    activeSubTab.value = 'exact';
    fetchAnswers();
  }
);

onMounted(fetchAnswers);
</script>

<template>
  <div class="flex flex-col gap-5">
    <StudioSection
      :title="$t('AI_AGENT.ANSWERS.LIBRARY.TITLE')"
      :description="$t('AI_AGENT.ANSWERS.LIBRARY.HELP')"
      icon="i-lucide-message-square-text"
    >
      <template v-if="activeSubTab !== 'unanswered'" #actions>
        <Button
          icon="i-lucide-upload"
          faded
          slate
          sm
          :label="$t('AI_AGENT.ANSWERS.IMPORT.BUTTON')"
          data-testid="answers-import"
          @click="importDialogRef.open()"
        />
        <Button
          icon="i-lucide-plus"
          sm
          :label="$t('AI_AGENT.ANSWERS.ADD')"
          data-testid="answers-add"
          @click="openCreateDialog"
        />
      </template>

      <TabBar
        :tabs="subTabItems"
        :initial-active-tab="activeSubTabIndex"
        @tab-changed="onSubTabChanged"
      />

      <template v-if="activeSubTab !== 'unanswered'">
        <StudioEmptyState
          v-if="!answersForSubTab.length && !answersFlags.isFetching"
          :icon="
            activeSubTab === 'keyword'
              ? 'i-lucide-key-round'
              : 'i-lucide-message-square-quote'
          "
          :title="$t('AI_AGENT.ANSWERS.EMPTY')"
          :description="
            activeSubTab === 'keyword'
              ? $t('AI_AGENT.ANSWERS.EMPTY_HELP_KEYWORD')
              : $t('AI_AGENT.ANSWERS.EMPTY_HELP_EXACT')
          "
        >
          <Button
            icon="i-lucide-plus"
            sm
            :label="$t('AI_AGENT.ANSWERS.ADD')"
            data-testid="answers-empty-add"
            @click="openCreateDialog"
          />
        </StudioEmptyState>
        <AnswerTable
          v-else
          :records="answersForSubTab"
          :match-type="activeSubTab"
          @edit="openEditDialog"
          @delete="openDeleteDialog"
          @toggle="toggleAnswer"
        />
      </template>

      <template v-else>
        <div class="flex flex-col gap-2">
          <div
            class="inline-flex flex-wrap self-start gap-1 p-1 rounded-xl bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
          >
            <button
              v-for="filter in CATEGORY_FILTERS"
              :key="filter.key"
              type="button"
              class="flex items-center gap-1.5 px-3 py-1 transition-colors duration-150 rounded-lg text-button-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand motion-reduce:transition-none"
              :class="
                unansweredCategory === filter.key
                  ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              :aria-pressed="unansweredCategory === filter.key"
              :data-testid="`unanswered-category-${filter.key}`"
              @click="selectUnansweredCategory(filter.key)"
            >
              {{ filter.label }}
              <span
                class="px-1.5 rounded-md tabular-nums text-label-small"
                :class="
                  filter.count && filter.key === 'gap'
                    ? 'bg-n-amber-5 text-n-amber-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ filter.count }}
              </span>
            </button>
          </div>
          <p class="text-label-small text-n-slate-10">
            {{
              $t(
                `AI_AGENT.UNANSWERED.CATEGORY_HELP.${unansweredCategory.toUpperCase()}`
              )
            }}
          </p>
        </div>

        <StudioEmptyState
          v-if="!unanswered.length && !unansweredFlags.isFetching"
          icon="i-lucide-inbox"
          :title="$t('AI_AGENT.UNANSWERED.EMPTY')"
        />
        <UnansweredTable
          v-else
          :records="unanswered"
          :can-convert="unansweredCategory === 'gap'"
          @convert="openConvertDialog"
          @dismiss="dismissRow"
        />

        <div
          v-if="unansweredTotalPages > 1"
          class="flex items-center justify-end gap-2"
        >
          <Button
            faded
            slate
            sm
            icon="i-lucide-chevron-left"
            :disabled="unansweredPage <= 1"
            data-testid="unanswered-prev-page"
            @click="gotoUnansweredPage(unansweredPage - 1)"
          />
          <span class="text-label-small text-n-slate-11">
            {{
              $t('AI_AGENT.UNANSWERED.PAGE', {
                page: unansweredPage,
                total: unansweredTotalPages,
              })
            }}
          </span>
          <Button
            faded
            slate
            sm
            icon="i-lucide-chevron-right"
            :disabled="unansweredPage >= unansweredTotalPages"
            data-testid="unanswered-next-page"
            @click="gotoUnansweredPage(unansweredPage + 1)"
          />
        </div>
      </template>
    </StudioSection>

    <ConfigForm
      :bot="bot"
      :fields="configFields"
      :sections="configSections"
      :title="$t('AI_AGENT.ANSWERS.MATCHING.TITLE')"
      :description="$t('AI_AGENT.ANSWERS.MATCHING.HELP')"
      icon="i-lucide-sliders-horizontal"
    />

    <Dialog
      ref="formDialogRef"
      type="edit"
      width="2xl"
      overflow-y-auto
      :title="
        editingAnswer
          ? $t('AI_AGENT.ANSWERS.EDIT_TITLE')
          : $t('AI_AGENT.ANSWERS.CREATE_TITLE')
      "
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <AnswerForm
        :key="`${formMatchType}-${editingAnswer ? editingAnswer.id : 'new'}`"
        :match-type="formMatchType"
        :record="editingAnswer"
        :is-saving="answersFlags.isCreating || answersFlags.isUpdating"
        @save="saveAnswer"
        @cancel="formDialogRef.close()"
      />
    </Dialog>

    <Dialog
      ref="convertDialogRef"
      type="edit"
      width="2xl"
      overflow-y-auto
      :title="$t('AI_AGENT.UNANSWERED.CONVERT_TITLE')"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <UnansweredConvertForm
        v-if="convertingRow"
        :key="convertingRow.id"
        :question="convertingRow.question"
        :is-saving="isConverting"
        @save="saveConversion"
        @cancel="convertDialogRef.close()"
      />
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('AI_AGENT.ANSWERS.DELETE.TITLE')"
      :description="
        $t('AI_AGENT.ANSWERS.DELETE.MESSAGE', {
          question: deletingAnswer?.question || deletingAnswer?.keyword,
        })
      "
      :confirm-button-label="$t('AI_AGENT.ANSWERS.DELETE.CONFIRM')"
      @confirm="confirmDelete"
    />

    <Dialog
      ref="importDialogRef"
      type="edit"
      width="2xl"
      :title="$t('AI_AGENT.ANSWERS.IMPORT.TITLE')"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <AnswerImportModal :bot="bot" @imported="onImported" />
    </Dialog>
  </div>
</template>
